import React, { useEffect, useState } from 'react'
import { Modal, Form, Select, InputNumber, Radio, Space, Divider, Row, Col, DatePicker } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { Commission } from '../types/commission.types'
import { productService } from '../../../shared/api/services/products.service'
import categoryService from '../../categories/services/category.service'
import { merchantsService } from '../../../shared/api/services/merchants.service'


const { Option } = Select

type CommissionScope = 'product' | 'category' | 'seller' | 'campaign' | 'global'

interface CommissionFormModalProps {
    open: boolean
    onCancel: () => void
    onSubmit: (values: any) => void
    initialValues?: Commission | null
}

export const CommissionFormModal: React.FC<CommissionFormModalProps> = ({
    open,
    onCancel,
    onSubmit,
    initialValues
}) => {
    const [form] = Form.useForm()
    const [scope, setScope] = useState<CommissionScope>('product')

    // Fetch categories from API
    const { data: categories, isLoading: loadingCategories } = useQuery({
        queryKey: ['categories'],
        queryFn: () => categoryService.getCategories(),
        enabled: open,
    })

    // Fetch products from API
    const { data: productsData, isLoading: loadingProducts } = useQuery({
        queryKey: ['products', 'approved'],
        queryFn: () => productService.getProducts({
            status: 'approved',
            per_page: 1000,
        }),
        enabled: open && scope === 'product',
    })

    // Fetch merchants from API
    const { data: merchantsData, isLoading: loadingMerchants } = useQuery({
        queryKey: ['merchants'],
        queryFn: () => merchantsService.getMerchants({ per_page: 1000 }),
        enabled: open && scope === 'seller',
    })



    useEffect(() => {
        console.log('🚀 useEffect triggered - open:', open, 'initialValues:', initialValues)

        if (open) {
            if (initialValues) {
                // Transform backend data to form format
                const backendData = initialValues as any

                console.log('🔍 Editing commission - Raw backend data:', backendData)

                // Helper to parse dates
                const parseDate = (dateStr: string | null | undefined) => {
                    if (!dateStr) return undefined
                    try {
                        return dayjs(dateStr)
                    } catch {
                        return undefined
                    }
                }

                // Handle product IDs - check multiple sources
                let productIds: number[] = []
                if (backendData.products && Array.isArray(backendData.products) && backendData.products.length > 0) {
                    productIds = backendData.products.map((p: any) => p.id)
                    console.log('✅ Found products array, IDs:', productIds)
                } else if (backendData.productId) {
                    // Old format - single product ID (camelCase)
                    productIds = [backendData.productId]
                    console.log('✅ Found single productId:', backendData.productId)
                } else if (backendData.product_id) {
                    // Old format - single product ID (snake_case)
                    productIds = [backendData.product_id]
                    console.log('✅ Found single product_id:', backendData.product_id)
                }

                const formData: any = {
                    scope: backendData.scope || 'product',
                    productIds: productIds,
                    categoryId: backendData.categoryId || backendData.category_id,
                    sellerId: backendData.sellerId || backendData.seller_id,
                    subscriptionPlanId: backendData.subscriptionPlanId || backendData.subscription_plan_id,
                    campaignId: backendData.campaignId || backendData.campaign_id,
                    commissionType: backendData.commissionType || backendData.commission_type,
                    commissionValue: backendData.commissionValue ?? backendData.commission_value,
                    minOrderValue: backendData.minOrderValue ?? backendData.min_order_value,
                    maxOrderValue: backendData.maxOrderValue ?? backendData.max_order_value,
                    minQuantity: backendData.minQuantity ?? backendData.min_quantity,
                    maxQuantity: backendData.maxQuantity ?? backendData.max_quantity,
                    priority: backendData.priority,
                    status: backendData.status || 'active',
                    effectiveFrom: parseDate(backendData.effectiveFrom || backendData.effective_from),
                    effectiveTo: parseDate(backendData.effectiveTo || backendData.effective_to),
                }

                form.setFieldsValue(formData)
                setScope(backendData.scope || 'product')
            } else {
                form.resetFields()
                form.setFieldsValue({
                    status: 'active',
                    scope: 'product',
                    priority: 5,
                    commissionType: 'percentage'
                })
                setScope('product')
            }
        }
    }, [open, initialValues, form])

    const handleOk = () => {
        form.validateFields().then(onSubmit)
    }

    return (
        <Modal
            title={initialValues ? "Edit Commission" : "Add New Commission"}
            open={open}
            onOk={handleOk}
            onCancel={onCancel}
            width={700}
            okText={initialValues ? "Update" : "Create"}
        >
            <Form form={form} layout="vertical">
                {/* Scope Section */}
                <Divider orientation="left">Commission Scope</Divider>

                <Form.Item
                    label="Apply Commission To"
                    name="scope"
                    rules={[{ required: true, message: 'Please select a scope' }]}
                >
                    <Radio.Group
                        value={scope}
                        onChange={(e) => {
                            const newScope = e.target.value
                            setScope(newScope)
                            form.setFieldsValue({ scope: newScope })
                            // Clear scope-specific fields
                            form.setFieldsValue({
                                productIds: undefined,
                                categoryId: undefined,
                                sellerId: undefined,

                                campaignId: undefined,
                            })
                        }}
                    >
                        <Space direction="vertical">
                            <Radio value="product">Specific Products</Radio>
                            <Radio value="category">Entire Category</Radio>
                            <Radio value="seller">Seller</Radio>

                            <Radio value="campaign">Campaign</Radio>
                            <Radio value="global">Global (Fallback)</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>

                {/* Conditional Scope Fields */}
                {scope === 'product' && (
                    <Form.Item
                        label="Products"
                        name="productIds"
                        rules={[{ required: true, type: 'array', min: 1, message: 'Please select at least one product' }]}
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select products"
                            loading={loadingProducts}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {productsData?.data?.map(product => (
                                <Option key={product.id} value={product.id}>
                                    {product.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {scope === 'category' && (
                    <Form.Item
                        label="Category"
                        name="categoryId"
                        rules={[{ required: true, message: 'Please select a category' }]}
                    >
                        <Select
                            placeholder="Select category"
                            loading={loadingCategories}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {categories?.map(category => (
                                <Option key={category.id} value={category.id}>
                                    {category.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                {scope === 'seller' && (
                    <Form.Item
                        label="Seller"
                        name="sellerId"
                        rules={[{ required: true, message: 'Please select a seller' }]}
                    >
                        <Select
                            placeholder="Select seller"
                            loading={loadingMerchants}
                            showSearch
                            filterOption={(input, option) =>
                                String(option?.children || '').toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {merchantsData?.data?.map(merchant => (
                                <Option key={merchant.merchant_id} value={merchant.merchant_id}>
                                    {merchant.business_name || merchant.owner_name || `Merchant #${merchant.merchant_id}`}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}
                {scope === 'campaign' && (
                    <Form.Item
                        label="Campaign ID"
                        name="campaignId"
                        rules={[{ required: true, message: 'Please enter campaign ID' }]}
                        help="Enter the campaign ID (campaign management coming soon)"
                    >
                        <InputNumber style={{ width: '100%' }} placeholder="Enter campaign ID" />
                    </Form.Item>
                )}

                {scope === 'global' && (
                    <div style={{ padding: '12px', background: '#f0f2f5', borderRadius: '4px', marginBottom: '16px' }}>
                        <p style={{ margin: 0, color: '#666' }}>
                            Global commission applies as a fallback when no other commission rules match.
                        </p>
                    </div>
                )}

                {/* Conditions Section */}
                <Divider orientation="left">Commission Conditions (Optional)</Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Min Order Value" name="minOrderValue">
                            <InputNumber style={{ width: '100%' }} prefix="₹" placeholder="0" min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Max Order Value" name="maxOrderValue">
                            <InputNumber style={{ width: '100%' }} prefix="₹" placeholder="No limit" min={0} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Min Quantity" name="minQuantity">
                            <InputNumber style={{ width: '100%' }} placeholder="0" min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Max Quantity" name="maxQuantity">
                            <InputNumber style={{ width: '100%' }} placeholder="No limit" min={0} />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Commission Details */}
                <Divider orientation="left">Commission Details</Divider>

                <Form.Item label="Type & Value">
                    <Space.Compact style={{ width: '100%' }}>
                        <Form.Item name="commissionType" noStyle rules={[{ required: true }]}>
                            <Select style={{ width: '40%' }} placeholder="Type">
                                <Option value="percentage">Percentage</Option>
                                <Option value="fixed">Fixed Amount</Option>
                            </Select>
                        </Form.Item>
                        <Form.Item name="commissionValue" noStyle rules={[{ required: true, message: 'Required' }]}>
                            <InputNumber
                                style={{ width: '60%' }}
                                placeholder="Value"
                                min={0}
                            />
                        </Form.Item>
                    </Space.Compact>
                </Form.Item>

                {/* Settings */}
                <Divider orientation="left">Settings</Divider>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Priority"
                            name="priority"
                            rules={[{ required: true, message: 'Required' }]}
                            help="1 = Highest, 9 = Lowest"
                        >
                            <InputNumber min={1} max={9} style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Status" name="status">
                            <Select>
                                <Option value="active">Active</Option>
                                <Option value="inactive">Inactive</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item label="Effective From" name="effectiveFrom">
                            <DatePicker
                                style={{ width: '100%' }}
                                showTime
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Effective To" name="effectiveTo">
                            <DatePicker
                                style={{ width: '100%' }}
                                showTime
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}
